import React, { useState } from "react"

/* Hallmark · component: ContactForm React (variantes "capture" y "full")
 * - capture: solo email + botón — para la sección "Save your seat" del home.
 * - full: nombre, email, teléfono opcional y mensaje — para la página Contact.
 * POST al endpoint REST bistro/v1/contact (ver snippet en functions.php).
 * Estados: idle · sending · success · error, con honeypot anti-spam.
 * Recibe props vía data-props (JSON) desde el template PHP. */

const INPUT =
  "w-full rounded-xl bg-white ring-1 ring-cafe-olla/20 px-4 py-3 text-carbon placeholder:text-cafe-olla/50 " +
  "focus:outline-none focus:ring-2 focus:ring-acento transition-shadow"

function ContactForm({ restUrl = "/wp-json/bistro/v1/contact", variant = "full" }) {
  const [status, setStatus] = useState("idle") // idle | sending | success | error
  const [values, setValues] = useState({ name: "", email: "", phone: "", message: "", website: "" })

  const isCapture = variant === "capture"

  const onChange = e => setValues(v => ({ ...v, [e.target.name]: e.target.value }))

  const onSubmit = async e => {
    e.preventDefault()
    if (values.website) return // honeypot: los bots lo llenan, los humanos no lo ven
    setStatus("sending")
    try {
      const res = await fetch(restUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, type: variant }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setStatus("success")
      setValues({ name: "", email: "", phone: "", message: "", website: "" })
    } catch (err) {
      console.error("ContactForm:", err)
      setStatus("error")
    }
  }

  if (status === "success") {
    return (
      <div className="rounded-2xl bg-white/80 ring-1 ring-cafe-olla/15 p-8 text-center" role="status">
        <p className="text-xl font-bold text-carbon">
          {isCapture ? "You're on the list! 🎉" : "Message sent! 🎉"}
        </p>
        <p className="mt-2 text-sm text-cafe-olla">
          {isCapture
            ? "We'll save you a seat. Specials and weekend surprises coming your way."
            : "We'll get back to you by phone or email. See you at the table."}
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={onSubmit} noValidate={false} className={isCapture ? "" : "grid gap-4"}>
      {/* Honeypot oculto */}
      <input
        type="text" name="website" tabIndex="-1" autoComplete="off"
        value={values.website} onChange={onChange}
        className="absolute left-[-9999px] w-px h-px overflow-hidden" aria-hidden="true"
      />

      {!isCapture && (
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-sm font-semibold text-carbon">Name</span>
            <input type="text" name="name" required value={values.name} onChange={onChange}
                   placeholder="Your name" className={`mt-1.5 ${INPUT}`} />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-carbon">Phone <span className="font-normal text-cafe-olla">(optional)</span></span>
            <input type="tel" name="phone" value={values.phone} onChange={onChange}
                   placeholder="(480) 000-0000" className={`mt-1.5 ${INPUT}`} />
          </label>
        </div>
      )}

      {isCapture ? (
        <div className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
          <label className="flex-1">
            <span className="sr-only">Email</span>
            <input type="email" name="email" required value={values.email} onChange={onChange}
                   placeholder="Your email" className={INPUT} />
          </label>
          <button type="submit" disabled={status === "sending"}
                  className="btn-order shrink-0 inline-flex justify-center items-center rounded-full text-white font-semibold px-7 py-3 disabled:opacity-60 disabled:pointer-events-none focus-visible:outline-2 focus-visible:outline-acento-oscuro focus-visible:outline-offset-2">
            {status === "sending" ? "Saving…" : "Save my seat"}
          </button>
        </div>
      ) : (
        <>
          <label className="block">
            <span className="text-sm font-semibold text-carbon">Email</span>
            <input type="email" name="email" required value={values.email} onChange={onChange}
                   placeholder="you@email.com" className={`mt-1.5 ${INPUT}`} />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-carbon">Message</span>
            <textarea name="message" required rows="4" value={values.message} onChange={onChange}
                      placeholder="Group reservations, catering, questions. Tell us everything."
                      className={`mt-1.5 ${INPUT} resize-y`} />
          </label>
          <button type="submit" disabled={status === "sending"}
                  className="btn-order justify-self-start inline-flex justify-center items-center rounded-full text-white font-semibold px-8 py-3 disabled:opacity-60 disabled:pointer-events-none focus-visible:outline-2 focus-visible:outline-acento-oscuro focus-visible:outline-offset-2">
            {status === "sending" ? "Sending…" : "Send message"}
          </button>
        </>
      )}

      {status === "error" && (
        <p className={`text-sm font-medium text-salsa-roja ${isCapture ? "mt-3 text-center" : ""}`} role="alert">
          Something went wrong. Please try again, or call us directly.
        </p>
      )}
    </form>
  )
}

export default ContactForm